import { FilterConnector } from '@core/models/entity/entity-selector-request.model';
import { foldForSearch } from '@core/utils/search-text.utils';
import { isNil } from '@core/utils/shared-utils';
import { parseFormattedDuration, parseISO8601Duration } from '@core/utils/time-utils';

import {
  DurationFilterCondition,
  FilterCondition,
  FilterConfiguration,
  FilterDataType,
  FilterGroup,
  FilterOperation,
  NumberFilterCondition,
  TextFilterCondition,
  UidFilterCondition
} from '@shared/components/filter-query-builder/filter.models';
import { pruneUnfilledConditions } from '@shared/utils/filter-condition.utils';

export type FilterValueAccessor<T> = (row: T, field: string) => unknown;

type FieldReader = (field: string) => unknown;
type RowTest = (read: FieldReader) => boolean;
type ValueTest = (value: unknown) => boolean;

const KEEP: ValueTest = () => true;

/**
 * Evaluates a filter builder configuration against rows held in the browser, for grids whose
 * rows the backend has never seen. Anything it does not understand — a data type, an
 * operation, a regex or a duration it cannot parse, a value the accessor fails to read —
 * keeps the row: hiding rows for a condition nobody can evaluate would look like data loss.
 *
 * The configuration is compiled once, so regexes and duration bounds are parsed per filter
 * change rather than per row.
 */
export function makeFilterPredicate<T>(
  config: FilterConfiguration | null,
  getValue: FilterValueAccessor<T>
): (row: T) => boolean {
  const pruned = pruneUnfilledConditions(config);
  const test = isNil(pruned) ? null : compileGroup(pruned.rootGroup);
  if (isNil(test)) return () => true;
  return (row) => test((field) => getValue(row, field));
}

/**
 * `null` when the group holds no enabled condition. The parent has to skip such a group
 * rather than read it as a match: under an OR, a vacuous `true` would let every row through.
 */
function compileGroup(group: FilterGroup): RowTest | null {
  const tests = group.conditions.flatMap((item): RowTest[] => {
    const test =
      'conditions' in item
        ? compileGroup(item)
        : item.enabled
          ? compileCondition(item)
          : null;
    return isNil(test) ? [] : [test];
  });
  if (tests.length === 0) return null;

  return group.connector === FilterConnector.OR
    ? (read) => tests.some((test) => test(read))
    : (read) => tests.every((test) => test(read));
}

function compileCondition(condition: FilterCondition): RowTest {
  let matches: ValueTest;
  try {
    matches = valueTest(condition);
  } catch {
    return () => true;
  }
  return (read) => {
    try {
      return matches(read(condition.field));
    } catch {
      return true;
    }
  };
}

function valueTest(condition: FilterCondition): ValueTest {
  switch (condition.dataType) {
    case FilterDataType.UID:
      return uidTest(condition as UidFilterCondition);
    case FilterDataType.NUMBER: {
      const { operation, value, minValue, maxValue } = condition as NumberFilterCondition;
      return numberTest(operation, value, minValue, maxValue);
    }
    case FilterDataType.DURATION: {
      const { operation, value, minValue, maxValue } = condition as DurationFilterCondition;
      return numberTest(
        operation,
        durationMinutes(value),
        durationMinutes(minValue),
        durationMinutes(maxValue)
      );
    }
    case FilterDataType.TEXT:
      return textTest(condition as TextFilterCondition);
    default:
      return KEEP;
  }
}

function asText(value: unknown): string | null {
  return isNil(value) || value === '' ? null : String(value);
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && !isNaN(value) ? value : undefined;
}

const CLOCK_TIME_REGEX = /^(\d{1,2}):(\d{2})$/;

// The builder's duration input is free text, so a bound on a time-of-day column is as likely
// to be typed `08:00` or `8h 30min` (the app's own duration rendering) as `PT8H30M`.
function durationMinutes(bound: string | undefined): number | undefined {
  if (isNil(bound)) return undefined;
  const trimmed = bound.trim();
  const clock = CLOCK_TIME_REGEX.exec(trimmed);
  if (!isNil(clock)) return Number(clock[1]) * 60 + Number(clock[2]);
  const formatted = parseFormattedDuration(trimmed);
  if (!isNil(formatted)) return formatted.totalMilliseconds / 60_000;
  return parseISO8601Duration(trimmed).totalMilliseconds / 60_000;
}

function uidTest(condition: UidFilterCondition): ValueTest {
  const expected = condition.value;
  const values = condition.values ?? [];

  switch (condition.operation) {
    case FilterOperation.EQUALS:
      return (value) => {
        const actual = asText(value);
        return actual !== null && actual === expected;
      };
    case FilterOperation.NOT_EQUALS:
      return (value) => asText(value) !== expected;
    case FilterOperation.IS_ANY_OF:
      return (value) => {
        const actual = asText(value);
        return actual !== null && values.includes(actual);
      };
    case FilterOperation.IS_NONE_OF:
      return (value) => {
        const actual = asText(value);
        return actual === null || !values.includes(actual);
      };
    case FilterOperation.IS_EMPTY:
      return (value) => asText(value) === null;
    case FilterOperation.IS_NOT_EMPTY:
      return (value) => asText(value) !== null;
    default:
      return KEEP;
  }
}

function numberTest(
  operation: FilterOperation,
  bound: number | undefined,
  min: number | undefined,
  max: number | undefined
): ValueTest {
  return (value) => {
    const actual = asNumber(value);
    if (operation === FilterOperation.IS_EMPTY) return isNil(actual);
    if (operation === FilterOperation.IS_NOT_EMPTY) return !isNil(actual);
    if (isNil(actual)) return operation === FilterOperation.NOT_EQUALS;

    switch (operation) {
      case FilterOperation.EQUALS:
        return actual === bound;
      case FilterOperation.NOT_EQUALS:
        return actual !== bound;
      case FilterOperation.GREATER_THAN:
        return !isNil(bound) && actual > bound;
      case FilterOperation.GREATER_THAN_OR_EQUALS:
        return !isNil(bound) && actual >= bound;
      case FilterOperation.LESS_THAN:
        return !isNil(bound) && actual < bound;
      case FilterOperation.LESS_THAN_OR_EQUALS:
        return !isNil(bound) && actual <= bound;
      case FilterOperation.BETWEEN:
        return !isNil(min) && !isNil(max) && actual >= min && actual <= max;
      default:
        return true;
    }
  };
}

function textTest(condition: TextFilterCondition): ValueTest {
  const { operation } = condition;

  if (operation === FilterOperation.IS_EMPTY) return (value) => asText(value) === null;
  if (operation === FilterOperation.IS_NOT_EMPTY) return (value) => asText(value) !== null;
  if (operation === FilterOperation.MATCHES_REGEX) {
    let regex: RegExp;
    try {
      regex = new RegExp(condition.value ?? '');
    } catch {
      return KEEP;
    }
    return (value) => {
      const actual = asText(value);
      return actual !== null && regex.test(actual);
    };
  }

  const keepsEmpty =
    operation === FilterOperation.NOT_EQUALS || operation === FilterOperation.NOT_CONTAINS;
  const needle = foldForSearch(condition.value);
  const compare = textComparison(operation, needle);

  return (value) => {
    const actual = asText(value);
    return actual === null ? keepsEmpty : compare(foldForSearch(actual));
  };
}

function textComparison(
  operation: FilterOperation,
  needle: string
): (haystack: string) => boolean {
  switch (operation) {
    case FilterOperation.EQUALS:
      return (haystack) => haystack === needle;
    case FilterOperation.NOT_EQUALS:
      return (haystack) => haystack !== needle;
    case FilterOperation.CONTAINS:
      return (haystack) => haystack.includes(needle);
    case FilterOperation.NOT_CONTAINS:
      return (haystack) => !haystack.includes(needle);
    case FilterOperation.STARTS_WITH:
      return (haystack) => haystack.startsWith(needle);
    case FilterOperation.ENDS_WITH:
      return (haystack) => haystack.endsWith(needle);
    default:
      return () => true;
  }
}
